/**
 * AP-773 — steps for annotations that come and go (§3.1 A1 create, A3 delete).
 *
 * A step holds a deep clone of the whole Cornerstone annotation object so it
 * can be put back exactly (`addAnnotation` keyed by its FrameOfReferenceUID),
 * plus the worksheet rows and Simpson / Doppler results on either side.
 * R17: applying a step that puts an annotation on screen selects it; applying
 * one that removes it leaves nothing selected.
 */
import type { Dispatch } from '@reduxjs/toolkit';
import { eventTarget, triggerEvent } from '@cornerstonejs/core';
import { annotation as csAnnotation, Enums } from '@cornerstonejs/tools';
import type { RootState } from '@/state/store';
import {
  deleteAnnotation,
  mapAnnotationToMeasurement,
  selectAnnotation,
} from '@/state/slices/measurementSession';
import { getBaseAnnotationStyle } from '@/features/study-detail/Viewer/utils/annotationStyle';
import { triggerAllViewports } from '@/features/study-detail/Viewer/utils/triggerAllViewports';
import { createWorksheetStep } from './worksheetSteps';
import {
  commitHistoryStep,
  runApplyingHistory,
  type HistoryLocation,
  type HistoryStep,
} from './historyStore';
import {
  restoreSessionResults,
  snapshotSessionResults,
  type SessionResultsSnapshot,
} from './sessionResults';
import { findPaneShowing, showStepLocation } from './viewerNavigation';
import { cloneAnnotationValue } from './annotationClone';
import {
  applyWorksheetSnapshot,
  getWorksheetSnapshot,
  type WorksheetSnapshot,
} from './worksheetApplier';

/** The minimal Redux surface a step needs. */
export interface HistoryStore {
  dispatch: Dispatch;
  getState: () => RootState;
}

export type AnnotationClone = {
  annotationUID: string;
  metadata: { FrameOfReferenceUID?: string; referencedImageId?: string };
  data: Record<string, unknown>;
} & Record<string, unknown>;

export function cloneAnnotation(uid: string): AnnotationClone | null {
  const live = csAnnotation.state.getAnnotation(uid);
  if (!live) return null;
  return cloneAnnotationValue(live as unknown as AnnotationClone);
}

/** The measurement name an annotation carries, for R20 labels: the chip
 *  abbreviation, else its label without the "AI:" prefix, else "annotation". */
export function annotationName(
  data: Record<string, unknown> | undefined
): string {
  const abbr = data?.abbreviation;
  if (typeof abbr === 'string' && abbr.trim()) return abbr.trim();
  const label = data?.measurementLabel;
  if (typeof label === 'string' && label.trim()) {
    return label.replace(/^AI:\s*/i, '').trim();
  }
  return 'annotation';
}

/** `video:<dicom>/frames/<N>` → the step location (frame 0-based, R6). */
export function locationFromAnnotation(
  clone: AnnotationClone
): HistoryLocation | undefined {
  const m = /^video:([^/]+)\/frames\/(\d+)$/.exec(
    clone.metadata.referencedImageId ?? ''
  );
  if (!m) return undefined;
  const dicomUuid = m[1];
  return {
    viewportId: findPaneShowing(dicomUuid) ?? '',
    dicomUuid,
    frame: Number(m[2]) - 1,
  };
}

function addAnnotationClone(clone: AnnotationClone, isSelected: boolean): void {
  const { annotationUID: uid } = clone;
  if (!csAnnotation.state.getAnnotation(uid)) {
    csAnnotation.state.addAnnotation(
      cloneAnnotationValue(clone) as never,
      clone.metadata.FrameOfReferenceUID as string
    );
  }
  csAnnotation.locking.setAnnotationLocked(uid, false);
  const live = csAnnotation.state.getAnnotation(uid);
  if (live) {
    csAnnotation.config.style.setAnnotationStyles(
      uid,
      getBaseAnnotationStyle(live, { isSelected })
    );
  }
  triggerAllViewports();
  triggerEvent(eventTarget, Enums.Events.ANNOTATION_MODIFIED, {
    annotation: live,
    changeType: 'StatsUpdated',
  });
}

function removeAnnotationById(uid: string): void {
  csAnnotation.config.style.setAnnotationStyles(uid, {});
  csAnnotation.state.removeAnnotation(uid);
  triggerAllViewports();
}

interface LifecycleStepParams {
  clone: AnnotationClone;
  worksheetBefore: WorksheetSnapshot | null;
  resultsBefore: SessionResultsSnapshot;
  store: HistoryStore;
  location?: HistoryLocation;
}

/** A3 — annotation deleted. Undo puts it back; redo removes it again. */
export function createDeleteStep({
  clone,
  worksheetBefore,
  resultsBefore,
  store,
  location,
}: LifecycleStepParams): HistoryStep {
  const uid = clone.annotationUID;
  let worksheetAfter: WorksheetSnapshot | null = null;
  let resultsAfter: SessionResultsSnapshot | null = null;
  return {
    action: 'A3',
    label: `${annotationName(clone.data)} deleted`,
    location,
    undo: () => {
      worksheetAfter ??= getWorksheetSnapshot();
      resultsAfter ??= snapshotSessionResults(store.getState(), uid);
      showStepLocation(location, () =>
        runApplyingHistory(() => {
          store.dispatch(selectAnnotation(uid));
          addAnnotationClone(clone, true);
          restoreSessionResults(store.dispatch, uid, resultsBefore);
          if (worksheetBefore) applyWorksheetSnapshot(worksheetBefore);
        })
      );
    },
    redo: () => {
      const results = resultsAfter;
      const worksheet = worksheetAfter;
      runApplyingHistory(() => {
        removeAnnotationById(uid);
        store.dispatch(selectAnnotation(null));
        if (results) restoreSessionResults(store.dispatch, uid, results);
        if (worksheet) applyWorksheetSnapshot(worksheet);
      });
    },
  };
}

/** A1 — annotation created. Undo removes it; redo puts it back. */
export function createCreateStep({
  clone,
  worksheetBefore,
  resultsBefore,
  store,
  location,
}: LifecycleStepParams): HistoryStep {
  const uid = clone.annotationUID;
  let worksheetAfter: WorksheetSnapshot | null = null;
  let resultsAfter: SessionResultsSnapshot | null = null;
  return {
    action: 'A1',
    label: `${annotationName(clone.data)} drawn`,
    location,
    undo: () => {
      if (!csAnnotation.state.getAnnotation(uid)) return false;
      worksheetAfter ??= getWorksheetSnapshot();
      resultsAfter ??= snapshotSessionResults(store.getState(), uid);
      runApplyingHistory(() => {
        removeAnnotationById(uid);
        store.dispatch(selectAnnotation(null));
        restoreSessionResults(store.dispatch, uid, resultsBefore);
        if (worksheetBefore) applyWorksheetSnapshot(worksheetBefore);
      });
      return true;
    },
    redo: () => {
      const results = resultsAfter;
      const worksheet = worksheetAfter;
      showStepLocation(location, () =>
        runApplyingHistory(() => {
          store.dispatch(selectAnnotation(uid));
          addAnnotationClone(clone, true);
          if (results) restoreSessionResults(store.dispatch, uid, results);
          if (worksheet) applyWorksheetSnapshot(worksheet);
        })
      );
      return true;
    },
  };
}

/**
 * The reader deletes an annotation (Delete / Backspace on the selected
 * trace). One call: record the A3 step, then perform the deletion the way the
 * viewer always has (clear style, remove, deselect, tell the worksheet).
 */
export function deleteAnnotationWithHistory(
  uid: string,
  store: HistoryStore
): boolean {
  const clone = cloneAnnotation(uid);
  if (!clone) return false;
  commitHistoryStep(
    createDeleteStep({
      clone,
      worksheetBefore: getWorksheetSnapshot(),
      resultsBefore: snapshotSessionResults(store.getState(), uid),
      store,
      location: locationFromAnnotation(clone),
    })
  );
  csAnnotation.config.style.setAnnotationStyles(uid, {});
  csAnnotation.state.removeAnnotation(uid);
  store.dispatch(selectAnnotation(null));
  store.dispatch(deleteAnnotation(uid));
  return true;
}

/** Delete a row's AI line as an undoable step; false if it isn't an AI line. */
export function deleteAILine(
  uid: string,
  store: HistoryStore,
  canonicalMeasurement: string
): boolean {
  const ann = csAnnotation.state.getAnnotation(uid);
  const before = getWorksheetSnapshot();
  if (!ann && before) {
    // Its image isn't on screen: delete it the way the viewer's Delete does.
    commitHistoryStep(createWorksheetStep('A5', before, 'AI line'));
    store.dispatch(
      mapAnnotationToMeasurement({ annotationUID: uid, canonicalMeasurement })
    );
    store.dispatch(deleteAnnotation(uid));
    return true;
  }
  if (!(ann?.data as { isAI?: boolean } | undefined)?.isAI) return false;
  if (!deleteAnnotationWithHistory(uid, store)) return false;
  triggerAllViewports();
  return true;
}
